import type {
  AddToCartContext,
  ClickContext,
  DeviceType,
  EventType,
  HomePageViewContext,
  ImpressionContext,
  PurchaseContext,
  ReferralContext,
  RemoveFromCartContext,
  RewardRedemptionContext,
  ReviewWriteContext,
  SignupContext,
  Source,
  ViewContext,
} from './constants';
import { EVENT_TYPE } from './constants';

// === Base metadata (common developer-supplied fields for all events) ===

/**
 * Base metadata interface for all personalization events.
 * @property {Source} [source] - The source of the event (e.g., 'web', 'mobile')
 */
export interface BaseMetadata {
  source?: Source;
}

// === Per-event metadata (developer-supplied, CommonMetadata is auto-injected) ===

/**
 * Metadata for product view events.
 * @property {string} [searchQuery] - Search query that led to this view
 * @property {number} [position] - Position in a list (e.g., search results, recommendations)
 * @property {string} [recommendationType] - Type of recommendation algorithm used
 * @property {string} [referrer] - Page that referred to this product view
 */
export interface ViewMetadata extends BaseMetadata {
  searchQuery?: string;
  position?: number;
  recommendationType?: string;
  referrer?: string;
}

/**
 * Metadata for product click events.
 * @property {number} [position] - Position of the clicked item in a list
 * @property {string} [recommendationType] - Type of recommendation algorithm used
 * @property {string} [sectionName] - Name of the section where the click occurred
 */
export interface ClickMetadata extends BaseMetadata {
  position?: number;
  recommendationType?: string;
  sectionName?: string;
}

/**
 * Metadata for product impression events (when product appears in viewport).
 * @property {number} position - Position of the impressed item in a list (required)
 * @property {string} [recommendationType] - Type of recommendation algorithm used
 * @property {string} [sectionName] - Name of the section where impression occurred
 * @property {number} [totalItems] - Total number of items in the list
 */
export interface ImpressionMetadata extends BaseMetadata {
  position: number;
  recommendationType?: string;
  sectionName?: string;
  totalItems?: number;
}

/**
 * Metadata for add to cart events.
 * @property {number} [priceAtAdd] - Price at the time of adding to cart
 * @property {string} [selectedVariant] - Selected product variant (e.g., "50ml", "Size L")
 * @property {string} [sku] - Product SKU for the specific variation
 */
export interface AddToCartMetadata extends BaseMetadata {
  priceAtAdd?: number;
  selectedVariant?: string;
  sku?: string;
}

/**
 * Metadata for purchase events.
 * @property {string} orderId - Unique order identifier (required)
 * @property {number} revenue - Total revenue for this purchase (required)
 * @property {string} currencyCode - Currency code (e.g., 'USD', 'KRW') (required)
 * @property {number} [discount] - Discount amount applied
 * @property {string} [couponCode] - Coupon code used for discount
 * @property {string} [paymentMethod] - Payment method used (e.g., 'credit_card', 'paypal')
 */
export interface PurchaseMetadata extends BaseMetadata {
  orderId: string;
  revenue: number;
  currencyCode: string;
  discount?: number;
  couponCode?: string;
  paymentMethod?: string;
}

/**
 * Metadata for user signup events.
 * @property {string} [referralCode] - Referral code used during signup
 */
export interface SignupMetadata extends BaseMetadata {
  referralCode?: string;
}

/**
 * Metadata for referral events.
 * @property {string} [referralCode] - Referral code that was shared
 */
export interface ReferralMetadata extends BaseMetadata {
  referralCode?: string;
}

/**
 * Metadata for reward redemption events.
 * @property {number} rewardAmount - Amount of reward redeemed (required)
 */
export interface RewardRedemptionMetadata extends BaseMetadata {
  rewardAmount: number;
}

/**
 * Metadata for product review write events.
 * @property {number} [rating] - Star rating given (e.g., 1-5)
 * @property {boolean} [hasPhoto] - Whether the review includes a photo
 * @property {string} [orderId] - Order ID associated with the review
 */
export interface ReviewWriteMetadata extends BaseMetadata {
  rating?: number;
  hasPhoto?: boolean;
  orderId?: string;
}

/**
 * Metadata for home page view events.
 */
export interface HomePageViewMetadata extends BaseMetadata {}

// === Discriminated Union (developer interface) ===

/**
 * Discriminated union type for all personalization events.
 * Each event type has specific required and optional properties.
 *
 * @example
 * // View event
 * const viewEvent: PersonalizationEvent = {
 *   eventType: 'view',
 *   productId: 'prod_123',
 *   context: 'product_detail',
 *   metadata: { position: 1 }
 * };
 *
 * @example
 * // Purchase event
 * const purchaseEvent: PersonalizationEvent = {
 *   eventType: 'purchase',
 *   productId: 'prod_123',
 *   context: 'checkout',
 *   quantity: 2,
 *   metadata: {
 *     orderId: 'order_456',
 *     revenue: 99.99,
 *     currencyCode: 'USD'
 *   }
 * };
 */
export type PersonalizationEvent =
  | {
      eventType: typeof EVENT_TYPE.VIEW;
      productId: string;
      context: ViewContext;
      metadata?: ViewMetadata;
    }
  | {
      eventType: typeof EVENT_TYPE.CLICK;
      productId: string;
      context: ClickContext;
      metadata?: ClickMetadata;
    }
  | {
      eventType: typeof EVENT_TYPE.IMPRESSION;
      productId: string;
      context: ImpressionContext;
      metadata?: ImpressionMetadata;
    }
  | {
      eventType: typeof EVENT_TYPE.ADD_TO_CART;
      productId: string;
      context: AddToCartContext;
      quantity: number;
      metadata?: AddToCartMetadata;
    }
  | {
      eventType: typeof EVENT_TYPE.REMOVE_FROM_CART;
      productId: string;
      context: RemoveFromCartContext;
      quantity?: number;
    }
  | {
      eventType: typeof EVENT_TYPE.PURCHASE;
      productId: string;
      context: PurchaseContext;
      quantity: number;
      metadata: PurchaseMetadata;
    }
  | {
      eventType: typeof EVENT_TYPE.SIGNUP;
      context: SignupContext;
      metadata?: SignupMetadata;
    }
  | {
      eventType: typeof EVENT_TYPE.REFERRAL;
      context: ReferralContext;
      metadata?: ReferralMetadata;
    }
  | {
      eventType: typeof EVENT_TYPE.REWARD_REDEMPTION;
      context: RewardRedemptionContext;
      metadata: RewardRedemptionMetadata;
    }
  | {
      eventType: typeof EVENT_TYPE.REVIEW_WRITE;
      productId: string;
      context: ReviewWriteContext;
      metadata?: ReviewWriteMetadata;
    }
  | {
      eventType: typeof EVENT_TYPE.HOME_PAGE_VIEW;
      context: HomePageViewContext;
      metadata?: HomePageViewMetadata;
    };

// === API payload (CommonMetadata merged by client before sending) ===

/**
 * Internal payload structure sent to the personalization API.
 * CommonMetadata (sessionId, deviceType) is automatically merged by the client.
 *
 * @property {string} actorId - Unique identifier for the user/actor
 * @property {EventType} eventType - Type of event being tracked
 * @property {string} [productId] - Product identifier (if applicable)
 * @property {string} context - Context where the event occurred
 * @property {number} [quantity] - Quantity (for cart/purchase events)
 * @property {Record<string, unknown>} metadata - Combined user-supplied and auto-injected metadata
 */
export interface PersonalizationEventPayload {
  actorId: string;
  eventType: EventType;
  productId?: string;
  context: string;
  quantity?: number;
  metadata: Record<string, unknown>;
}

// === Auto-injected common metadata ===

/**
 * Metadata automatically injected into every event by the client.
 *
 * @property {string} sessionId - Current session identifier
 * @property {DeviceType} deviceType - Device type (mobile/tablet/desktop)
 */
export interface CommonMetadata {
  sessionId: string;
  deviceType: DeviceType;
}

// === Injected function signatures ===

/**
 * Function to resolve the actor (user) ID.
 * Should fetch or generate a unique identifier for the current user.
 *
 * @param {string} [currentActorId] - Optional currently stored actor ID for merge
 * @returns {Promise<{ actorId: string }>} Promise resolving to an object containing the actor ID
 *
 * @example
 * const resolveActor = async (currentActorId) => {
 *   const response = await fetch('/api/actors/resolve', {
 *     method: 'POST',
 *     body: JSON.stringify(currentActorId ? { actorId: currentActorId } : {})
 *   });
 *   return response.json(); // { actorId: 'user_123' }
 * };
 */
export type ResolveActorFn = (currentActorId?: string) => Promise<{ actorId: string }>;

/**
 * Function to send events to the personalization API.
 *
 * @param {PersonalizationEventPayload} payload - Event payload to send
 * @returns {Promise<void>} Promise that resolves when the event is sent
 *
 * @example
 * const sendEvent = async (payload) => {
 *   await fetch('/api/personalization/event', {
 *     method: 'POST',
 *     body: JSON.stringify(payload)
 *   });
 * };
 */
export type SendEventFn = (payload: PersonalizationEventPayload) => Promise<void>;

// === Storage adapter interface ===

/**
 * Interface for storage adapters that persist personalization data.
 * Implementations include WebPersonalizationStorageAdapter (localStorage/sessionStorage),
 * AsyncStorageAdapter (React Native), and MemoryStorageAdapter (testing).
 *
 * @interface IPersonalizationStorageAdapter
 *
 * @example
 * class CustomStorageAdapter implements IPersonalizationStorageAdapter {
 *   getActorId() { return localStorage.getItem('actorId'); }
 *   setActorId(id) { localStorage.setItem('actorId', id); }
 *   // ... implement other methods
 * }
 */
export interface IPersonalizationStorageAdapter {
  /**
   * Retrieves the stored actor ID.
   * @returns {string | null} Actor ID or null if not found
   */
  getActorId(): string | null;

  /**
   * Stores the actor ID.
   * @param {string} actorId - Actor ID to store
   */
  setActorId(actorId: string): void;

  /**
   * Removes the stored actor ID.
   */
  removeActorId(): void;

  /**
   * Retrieves the current session ID.
   * @returns {string | null} Session ID or null if not found
   */
  getSessionId(): string | null;

  /**
   * Stores the session ID.
   * @param {string} sessionId - Session ID to store
   */
  setSessionId(sessionId: string): void;

  /**
   * Removes the stored session ID.
   */
  removeSessionId(): void;

  /**
   * Retrieves the timestamp of the last session activity.
   * @returns {number | null} Timestamp in milliseconds or null if not found
   */
  getSessionTimestamp(): number | null;

  /**
   * Stores the timestamp of the current session activity.
   * @param {number} timestamp - Timestamp in milliseconds (Date.now())
   */
  setSessionTimestamp(timestamp: number): void;

  /**
   * Removes the stored session timestamp.
   */
  removeSessionTimestamp(): void;
}
